import { ref, computed } from 'vue'
import aplosApi from '@/services/aplosApi'
import { unixSecondsToLocalizedDateString, searchColorMap } from '@/utils'
import { getExpirationStatus, formatMaxExecutions, capitalizeStatus } from '@/utils/subscriptionUtils'
import type { TenantsSubscription } from '@/types'

/**
 * Composable for fetching and exposing reactive subscription state.
 *
 * @param tenantId - The tenant ID to fetch the active subscription for
 * @returns Reactive subscription state, formatted display values, and fetch/retry methods
 */
export function useSubscription(tenantId: string) {
  const loading = ref(false)
  const error = ref<string | null>(null)
  const subscription = ref<TenantsSubscription | null>(null)

  // Formatted computed values
  const planCode = computed(() => subscription.value?.planCode ?? '')
  const status = computed(() =>
    subscription.value?.status ? capitalizeStatus(subscription.value.status) : ''
  )
  const subscriptionId = computed(() => subscription.value?.id ?? '')
  const periodStart = computed(() =>
    unixSecondsToLocalizedDateString(subscription.value?.currentPeriodStartUtcTs ?? null)
  )
  const periodEnd = computed(() =>
    unixSecondsToLocalizedDateString(subscription.value?.currentPeriodEndUtcTs ?? null)
  )
  const seatCount = computed(() => subscription.value?.seatCount ?? 0)
  const maxExecutions = computed(() =>
    formatMaxExecutions(subscription.value?.metadata?.maxExecutions ?? 0)
  )
  const maxProfiles = computed(() => subscription.value?.metadata?.maxProfiles ?? 0)
  const billingInterval = computed(() => subscription.value?.billingInterval ?? '')

  // Expiration warning computed from getExpirationStatus
  const expirationWarning = computed(() =>
    getExpirationStatus(subscription.value?.currentPeriodEndUtcTs)
  )

  // Status color with expiration override
  const statusColor = computed(() => {
    const warning = expirationWarning.value
    if (warning === 'expired') return searchColorMap('expired')
    if (warning === 'renew_soon') return searchColorMap('renew soon')
    const rawStatus = subscription.value?.status?.toLowerCase() ?? ''
    return searchColorMap(rawStatus)
  })

  async function fetchSubscription(): Promise<void> {
    loading.value = true
    error.value = null
    subscription.value = null

    try {
      const response = await aplosApi.getActiveSubscription(tenantId)
      const data = response?.data

      if (!data) {
        error.value = 'No active subscription found for this tenant. Please contact your administrator.'
        subscription.value = null
      } else {
        subscription.value = data
      }
    } catch (e: any) {
      error.value = 'Unable to load subscription. Please try again or contact support.'
      subscription.value = null
    } finally {
      loading.value = false
    }
  }

  async function retry(): Promise<void> {
    await fetchSubscription()
  }

  // Fetch on invocation
  fetchSubscription()

  return {
    // Reactive state
    loading,
    error,
    subscription,

    // Formatted display values
    planCode,
    status,
    statusColor,
    subscriptionId,
    periodStart,
    periodEnd,
    seatCount,
    maxExecutions,
    maxProfiles,
    billingInterval,

    // Expiration state
    expirationWarning,

    // Methods
    fetchSubscription,
    retry,
  }
}
